// Reassign, split or discard part of a span. Nothing is overwritten: each correction is a new, revertible record.
import { useState } from 'react';
import { isoLocalMinute, parseLocal } from '../lib/calendar.ts';
import { activeProjects, displayTitle, project as findProject } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { Field, Select, Sheet, TextInput, onValue } from '../components/common.tsx';
import { useFmt, useSession, useStore } from '../store/hooks.tsx';
import { Button, SegmentedControl } from '../ui/ds.tsx';

type Mode = 'reassign' | 'split' | 'discard';

export function SpanEdit({ sessionId, range, mode: initial }: { sessionId: string; range: { start: number; end: number }; mode: Mode }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const tz = store.timeZone;
  const session = useSession(sessionId).data?.sessions.find((s) => s.id === sessionId);
  const [mode, setMode] = useState<Mode>(initial);
  const [from, setFrom] = useState(isoLocalMinute(initial === 'split' ? range.start + (range.end - range.start) / 2 : range.start, tz).slice(11));
  const [to, setTo] = useState(isoLocalMinute(range.end, tz).slice(11));
  const [projectId, setProjectId] = useState<string>('');
  const [note, setNote] = useState('');
  const close = () => store.closeSheet();
  // Times on the span's own days; a span across midnight takes the later day for times before its start.
  const at = (hhmm: string, base: number) => {
    const day = isoLocalMinute(base, tz).slice(0, 10);
    const value = parseLocal(`${day}T${hhmm}`, tz);
    if (value !== null && value < range.start - 60_000) return parseLocal(`${isoLocalMinute(range.end, tz).slice(0, 10)}T${hhmm}`, tz);
    return value;
  };
  const clamp = (v: number | null) => (v === null ? null : Math.min(range.end, Math.max(range.start, v)));
  const start = clamp(at(from, range.start));
  const end = mode === 'split' ? range.end : clamp(at(to, range.start));
  // Minute inputs: the span's own end may carry seconds; a whole-minute "Bis" at the end means the end.
  const endValue = end !== null && mode !== 'split' && to === isoLocalMinute(range.end, tz).slice(11) ? range.end : end;
  const startValue = start !== null && from === isoLocalMinute(range.start, tz).slice(11) && mode !== 'split' ? range.start : start;
  const ok = startValue !== null && endValue !== null && endValue > startValue;
  const valid = ok && (mode === 'discard' || (projectId !== '' && (mode === 'split' || projectId !== session?.projectId)));
  const current = findProject(store.ws, session?.projectId)?.name ?? t('ohne Projekt');
  const target = findProject(store.ws, projectId)?.name;
  const duration = ok ? fmt.duration((endValue as number) - (startValue as number)) : '';
  const preview = !ok ? t('Das Ende muss nach dem Beginn liegen.')
    : mode === 'discard' ? t('%@ zählen nicht mehr. Die Events bleiben erhalten, du kannst es jederzeit rückgängig machen.', duration)
    : !target ? t('Wähle das Projekt, zu dem die Zeit gehört.')
    : mode === 'reassign' && projectId === session?.projectId ? t('Die Zeit gehört schon zu %@.', target)
    : t('%@ wechseln von %@ zu %@. Abgerechnet wird danach in %@, zusammengeführt mit allem anderen dort.', duration, current, target, target);
  const apply = () => {
    if (!valid || startValue === null || endValue === null) return;
    const kind = mode === 'discard' ? 'discard' : 'reassign';
    void store.apply(m.correctSession(store.actor(), sessionId, kind, { start: startValue, end: endValue }, kind === 'reassign' ? projectId : null, note, store.clock()));
    close();
  };
  const title = mode === 'reassign' ? t('Zeit einem anderen Projekt zuordnen') : mode === 'split' ? t('Span teilen') : t('Zeit verwerfen');
  return (
    <Sheet title={title} subtitle={t('%@ · Span %@–%@', session ? displayTitle(session) : '', fmt.time(range.start), fmt.time(range.end))} onClose={close}
      footer={<>
        <span className="spacer" />
        <Button variant="plain" onClick={close}>{t('Abbrechen')}</Button>
        <Button variant={mode === 'discard' ? 'danger' : 'primary'} disabled={!valid} onClick={apply}>{mode === 'discard' ? t('Verwerfen') : t('Anwenden')}</Button>
      </>}>
      <SegmentedControl<Mode> label={t('Korrektur')} value={mode} onChange={setMode}
        options={[{ value: 'reassign', label: t('Umhängen') }, { value: 'split', label: t('Teilen') }, { value: 'discard', label: t('Verwerfen') }]} />
      <div className="form">
        {mode === 'split' ? (
          <Field label={t('Teilen um')}><TextInput type="time" value={from} onChange={onValue(setFrom)} /></Field>
        ) : (
          <div className="form-row">
            <Field label={t('Von')}><TextInput type="time" value={from} onChange={onValue(setFrom)} /></Field>
            <Field label={t('Bis')}><TextInput type="time" value={to} onChange={onValue(setTo)} /></Field>
          </div>
        )}
        {mode !== 'discard' ? (
          <Field label={mode === 'split' ? t('Zweiter Teil gehört zu') : t('Projekt')}>
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{t('Projekt wählen')}</option>
              {activeProjects(store.ws).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        ) : null}
        <Field label={t('Notiz')}><TextInput value={note} placeholder={t('optional, z. B. „Recherche für Nordlicht“')} onChange={onValue(setNote)} /></Field>
        <p className={ok ? 'note' : 'field__error'}>{preview}</p>
      </div>
    </Sheet>
  );
}
